import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Lock } from "lucide-react";
import { listLeads, type LeadRow } from "@/lib/leads.functions";

export const Route = createFileRoute("/lp/leads")({
  head: () => ({
    meta: [
      { title: "Inskickade formulär | Svensk Låneguide" },
      { name: "description", content: "Intern översikt över inskickade låneformulär." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Inskickade formulär" },
      { property: "og:description", content: "Intern översikt över inskickade låneformulär." },
      { property: "og:type", content: "website" },
    ],
  }),
  component: LeadsPage,
});

const fmt = (n: number | null) => (n === null ? "–" : new Intl.NumberFormat("sv-SE").format(n) + " kr");
const ja = (v: boolean | null) => (v === null ? "–" : v ? "Ja" : "Nej");

function LeadsPage() {
  const fetchLeads = useServerFn(listLeads);
  const [password, setPassword] = useState("");
  const [leads, setLeads] = useState<LeadRow[] | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (pw: string) => {
    setLoading(true);
    setError("");
    const res = await fetchLeads({ data: { password: pw } });
    setLoading(false);
    if (!res.ok) {
      setError("Fel lösenord eller inga uppgifter kunde hämtas.");
      return;
    }
    setLeads(res.leads);
  };

  if (!leads) {
    return (
      <main className="grid min-h-screen place-items-center bg-background px-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void load(password);
          }}
          className="w-full max-w-sm rounded-md border border-border bg-card p-6 shadow-elevated"
        >
          <div className="flex items-center gap-2 font-display text-xl font-extrabold text-foreground">
            <Lock className="h-5 w-5 text-primary" /> Inskickade formulär
          </div>
          <p className="mt-1 text-sm text-muted-foreground">Ange lösenord för att se alla leads.</p>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Lösenord"
            className="mt-4 w-full rounded-md border border-input bg-card px-4 py-3 text-[15px] outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
          />
          {error && <p className="mt-2 text-sm font-semibold text-destructive">{error}</p>}
          <button
            type="submit"
            disabled={loading || !password}
            className="mt-4 w-full rounded-md bg-cta py-3 text-base font-bold text-cta-foreground disabled:opacity-50"
          >
            {loading ? "Hämtar…" : "Logga in"}
          </button>
        </form>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background px-4 py-8 sm:px-6">
      <div className="mx-auto max-w-7xl">
        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4">
          <h1 className="truncate font-display text-2xl font-extrabold text-foreground sm:text-3xl">
            Inskickade formulär ({leads.length})
          </h1>
          <button
            onClick={() => void load(password)}
            className="shrink-0 rounded-md border border-border bg-card px-4 py-2 text-sm font-semibold hover:bg-muted"
          >
            Uppdatera
          </button>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Senaste 24 timmarna:{" "}
          <span className="font-semibold text-foreground">
            {leads.filter((l) => Date.now() - new Date(l.created_at).getTime() < 86400000).length}
          </span>{" "}
          nya inskick
        </p>


        {leads.length === 0 && <p className="mt-6 text-sm text-muted-foreground">Inga formulär inskickade ännu.</p>}

        {/* Mobil: kort */}
        <div className="mt-6 space-y-4 lg:hidden">
          {leads.map((l) => (
            <div key={l.id} className="rounded-md border border-border bg-card p-4 text-sm">
              <div className="flex items-baseline justify-between gap-3">
                <span className="font-display text-base font-extrabold text-foreground">{l.name}</span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {new Date(l.created_at).toLocaleString("sv-SE")}
                </span>
              </div>
              <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
                <Row k="E-post" v={l.email} />
                <Row k="Mobil" v={l.phone} />
                <Row k="Belopp" v={fmt(l.amount)} />
                <Row k="Inkomst" v={fmt(l.income)} />
                <Row k="Mål" v={l.goal ?? "–"} />
                <Row k="Skuldtyper" v={(l.debt_types ?? []).join(", ") || "–"} />
                <Row k="Kronofogden" v={ja(l.kfm)} />
                <Row k="Anmärkning" v={ja(l.remarks)} />
                <Row k="UC senaste året" v={l.uc_count ?? "–"} />
                <Row k="Förslag" v={(l.matched_lenders ?? []).join(", ") || "–"} />
                <Row k="Källa" v={l.source} />
              </dl>
            </div>
          ))}
        </div>

        {/* Desktop: tabell */}
        <div className="mt-6 hidden overflow-x-auto rounded-md border border-border bg-card lg:block">
          <table className="w-full text-left text-sm">
            <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                {["Datum", "Namn", "E-post", "Mobil", "Belopp", "Inkomst", "Mål", "Skuldtyper", "KFM", "Anm.", "UC", "Förslag"].map(
                  (h) => (
                    <th key={h} className="whitespace-nowrap px-3 py-2 font-semibold">
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-t border-border/60 align-top">
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">
                    {new Date(l.created_at).toLocaleString("sv-SE")}
                  </td>
                  <td className="px-3 py-2 font-semibold">{l.name}</td>
                  <td className="px-3 py-2">{l.email}</td>
                  <td className="whitespace-nowrap px-3 py-2">{l.phone}</td>
                  <td className="whitespace-nowrap px-3 py-2">{fmt(l.amount)}</td>
                  <td className="whitespace-nowrap px-3 py-2">{fmt(l.income)}</td>
                  <td className="px-3 py-2">{l.goal ?? "–"}</td>
                  <td className="px-3 py-2">{(l.debt_types ?? []).join(", ") || "–"}</td>
                  <td className="px-3 py-2">{ja(l.kfm)}</td>
                  <td className="px-3 py-2">{ja(l.remarks)}</td>
                  <td className="px-3 py-2">{l.uc_count ?? "–"}</td>
                  <td className="px-3 py-2">{(l.matched_lenders ?? []).join(", ") || "–"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </main>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <>
      <dt className="text-muted-foreground">{k}</dt>
      <dd className="min-w-0 break-words font-medium text-foreground">{v}</dd>
    </>
  );
}
